import React, { memo, useMemo } from "react";
import moment from "moment";
import styles from "./Calendar.module.css";
import { DATE_KEY_FORMAT, PERIODS } from "./timeTableUtils";

/**
 * byDay: { [isoWeekday]: { [period]: item } } – built once per week by groupWeekByDay.
 * undefined while the week is still loading.
 */
const WeeklyTimetableModal = ({ date, byDay, onClose }) => {
  const loading = byDay === undefined;
  const { weekDays, weekRangeText } = useMemo(() => {
    const weekStart = moment(date).startOf("isoWeek");
    const weekEnd = weekStart.clone().add(6, "days");
    return {
      weekDays: Array.from({ length: 7 }, (_, i) => {
        const day = weekStart.clone().add(i, "days");
        return {
          weekday: i + 1,
          dateKey: day.format(DATE_KEY_FORMAT),
          label: day.format("DD-MMM(ddd)"),
        };
      }),
      weekRangeText: `${weekStart.format("DD-MM-YYYY")} to ${weekEnd.format("DD-MM-YYYY")}`,
    };
  }, [date]);

  // Only list the days the API returned timetable data for (e.g. hide Sat/Sun)
  const visibleDays = useMemo(
    () => (loading ? [] : weekDays.filter((day) => byDay[day.weekday])),
    [loading, weekDays, byDay],
  );

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* HEADER */}
        <h2 className={styles.weeklyTitle}>
          Weekly Timetable ({weekRangeText})
        </h2>
        <hr />
        {/* TABLE */}
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Day</th>
                {PERIODS.map((period) => (
                  <th key={period}>Period {period}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {visibleDays.length > 0 ? (
                visibleDays.map((day, idx) => {
                  const periods = byDay[day.weekday];
                  return (
                    <tr
                      key={day.dateKey}
                      className={idx % 2 ? styles.gray : ""}
                    >
                      <td>{day.label}</td>
                      {PERIODS.map((period) => (
                        <td key={period}>
                          {periods[period]?.subject_name || "--"}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={PERIODS.length + 1}
                    style={{ textAlign: "center" }}
                  >
                    {loading ? "Loading..." : "No Data Available"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* FOOTER */}
        <div className={styles.footer}>
          <button className={styles.closeBtn} onClick={onClose}>
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(WeeklyTimetableModal);
